import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { PageHeader, Skeleton } from "../../components/UI";
import { useLanguage } from "../../contexts/LanguageContext";
import { useFormat } from "../../hooks/useFormat";
import { apiFetch } from "../../lib/api";
import { Rows, Surface } from "./ui";

const Metrics = styled.dl`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 24px;
  div {
    display: grid;
    gap: 2px;
  }
  dt {
    font-size: 14px;
    color: ${(p) => p.theme.colors.textSecondary};
  }
  dd.v {
    font-size: 32px;
    line-height: 40px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
  dd.h {
    font-size: 13px;
    color: ${(p) => p.theme.colors.textMuted};
  }
  @media (max-width: 767px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px 16px;
  }
`;
const Two = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
  }
  .head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 8px;
    a {
      display: inline-flex;
      align-items: center;
      min-height: 44px;
      font-size: 14px;
      font-weight: 600;
    }
  }
`;

type Data = { users: number; maps: number; complaintsOpen: number; tickets: any[]; complaints: any[] };

export default function Overview() {
  const { t } = useLanguage();
  const f = useFormat();
  const [d, setD] = useState<Data | null>(null);
  const [bad, setBad] = useState<string[]>([]);

  useEffect(() => {
    let alive = true;
    const get = async (path: string) => {
      const r = await apiFetch(path);
      if (!r.ok) throw new Error(path);
      return r.json();
    };
    (async () => {
      const failed: string[] = [];
      const safe = async <T,>(p: string, fallback: T): Promise<T> => {
        try {
          return await get(p);
        } catch {
          failed.push(p);
          return fallback;
        }
      };
      const [users, maps, comps, tickets] = await Promise.all([
        safe<any>("/admin/users?page=1&pageSize=1", { total: 0 }),
        safe<any>("/admin/maps?page=1&pageSize=200", { items: [] }),
        safe<any>("/admin/complaints?page=1&pageSize=100", { items: [], total: 0 }),
        safe<any>("/admin/support", { items: [] }),
      ]);
      if (!alive) return;
      setBad(failed);
      setD({
        users: Number(users.total) || 0,
        maps: (maps.items || []).length,
        complaintsOpen: (comps.items || []).filter((c: any) => c.status === "open").length,
        tickets: tickets.items || [],
        complaints: (comps.items || []).slice(0, 3),
      });
    })();
    return () => {
      alive = false;
    };
  }, []);

  const v = (n: number | undefined) => (d ? f.number(n, 0) : <Skeleton $h={32} $w="50%" />);
  return (
    <>
      <PageHeader title={t("adm.overview")} />
      <Surface>
        <Metrics>
          <div>
            <dt>{t("adm.ov_users")}</dt>
            <dd className="v">{v(d?.users)}</dd>
          </div>
          <div>
            <dt>{t("adm.ov_maps")}</dt>
            <dd className="v">{v(d?.maps)}</dd>
          </div>
          <div>
            <dt>{t("adm.ov_complaints")}</dt>
            <dd className="v">{v(d?.complaintsOpen)}</dd>
          </div>
          <div>
            <dt>{t("adm.ov_tickets")}</dt>
            <dd className="v">{v(d?.tickets.length)}</dd>
            <dd className="h">{t("adm.ov_scope_tickets")}</dd>
          </div>
        </Metrics>
        {bad.length > 0 && (
          <p role="alert" style={{ marginTop: 16, fontSize: 14, color: "inherit", opacity: 0.8 }}>
            {t("adm.load_error")}: {bad.join(", ")}
          </p>
        )}
      </Surface>
      <Two>
        <Surface>
          <div className="head">
            <h2>{t("adm.ov_recent_complaints")}</h2>
            <Link to="/admin/moderation">{t("adm.ov_open")}</Link>
          </div>
          <Rows>
            {(d?.complaints || []).map((c: any) => (
              <li key={c.id} style={{ gridTemplateColumns: "minmax(0, 1fr)" }}>
                <div className="name">{c.subject}</div>
                <div className="sub">{f.date(c.createdAt)}</div>
              </li>
            ))}
            {d && d.complaints.length === 0 && <li className="sub">{t("adm.mod_empty")}</li>}
          </Rows>
        </Surface>
        <Surface>
          <div className="head">
            <h2>{t("adm.ov_recent")}</h2>
            <Link to="/admin/support">{t("adm.ov_open")}</Link>
          </div>
          <Rows>
            {(d?.tickets || []).slice(0, 3).map((x: any) => (
              <li key={x.id} style={{ gridTemplateColumns: "minmax(0, 1fr)" }}>
                <div className="name">
                  {t("adm.sup_number", { n: x.number })} · {x.subject}
                </div>
                <div className="sub">
                  {x.name} · {f.date(x.createdAt)}
                </div>
              </li>
            ))}
            {d && d.tickets.length === 0 && <li className="sub">{t("adm.sup_empty")}</li>}
          </Rows>
        </Surface>
      </Two>
    </>
  );
}
